import React, { FC, useCallback, useMemo, useState } from 'react'
import { styled, CardTitle, TextButton } from '~/app/components'
import Card from '~/app/components/order-list/common/Card'
import OrderCardItem from '~/app/components/order-list/OrderListCardItem'
import useIsoToFormat from '~/app/utils/useIsoToFormat'
import getCartOrderConverter from '~/app/components/order-converter/getCartOrderConverter'
import { Props as OrderCardItemProps } from '~/app/components/order-list/common/OrderCardItem'
import { sendKinesisProductLinkClick } from '~/app/kinesis/order-detail-page'
import { CartOrdersV3Query } from '~/app/components/order-list/hooks/useOrderListLoader'
import useHandleOrderItemButtonClick from '~/app/components/order-list/common/useHandleOrderItemButtonClick'
import { sendKinesisClickLogTimeoutAfter } from '~/app/utils/timeout'
import { CartOrderListPageLog } from '~/app/kinesis/protobuf'
import ShowMore from '~/app/components/order-list/common/OrderListShowMore'
import { isMigratedToProtobuf } from './utils'

const OrderListCardItem = styled(OrderCardItem)`
  ::after {
    margin-left: -2rem;
    width: calc(100% + 4rem);
  }

  @media (min-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
    width: 50%;

    :nth-of-type(2n) {
      padding-right: 2rem;
      margin-right: 0;
    }

    :nth-of-type(2n - 1) {
      padding-left: 2rem;
      margin-left: 0;
    }
  }
`

const NewCard = styled(Card)`
  overflow: visible;
  z-index: auto;
  :nth-of-type(1) {
    margin-top: 0;
  }
`
const OrderListTextButton = styled(props => <TextButton {...props} />)`
  height: 3.3rem;
`

const OrderListShowMore = styled(ShowMore)`
  border-top: 0.1rem solid ${({ theme }) => theme.color.gray200};
`

const DEFAULT_CARD_ITEM_COUNT = 4 // 예약내역 기본 출력 아이템 개수

type Order = CartOrdersV3Query[number]
type Props = {
  currentAt: string
  order: Order
  index: number
  onDetailsButtonClick?: (id: string) => void
  onProductClick?: (id: string, url: string, deeplink?: string) => void
  onProductImpr?: (id: string) => void
  onCancelRefreshButtonClick?: () => void
  storePreviewOrderListInfo: () => void
}
const OrderListCard: FC<Props> = ({
  currentAt,
  order,
  index,
  onDetailsButtonClick,
  onProductClick,
  onProductImpr,
  onCancelRefreshButtonClick,
  storePreviewOrderListInfo,
}) => {
  const [isShowMore, setIsShowMore] = useState(false)
  const { toCartDomesticOrder, toCartLeisureOrder, toCartTrainOrder, toCartBusOrder } =
    getCartOrderConverter()
  const categoryOrderToCardOrder = useCallback(
    categoryOrder =>
      categoryOrder.__typename === 'CartDomesticOrder'
        ? toCartDomesticOrder(categoryOrder, currentAt, {
            id: order.id,
            hashOrderId: order.hashOrderId,
          })
        : categoryOrder.__typename === 'CartLeisureProductOrder'
          ? toCartLeisureOrder(
              categoryOrder,
              currentAt,
              {
                hashOrderId: order.hashOrderId,
                hashCategoryOrderId: categoryOrder.hashOrderId,
              },
              false
            )
          : categoryOrder.__typename === 'CartTrainOrder'
            ? toCartTrainOrder(categoryOrder, currentAt, {
                hashOrderId: order.hashOrderId,
                hashCategoryOrderId: categoryOrder.hashOrderId,
              })
            : categoryOrder.__typename === 'CartBusOrder'
              ? toCartBusOrder(categoryOrder, currentAt, {
                  hashOrderId: order.hashOrderId,
                  hashCategoryOrderId: categoryOrder.hashOrderId,
                })
              : null,
    [currentAt]
  )

  const { handleOrderItemButtonClick } = useHandleOrderItemButtonClick()

  const onButtonClick = useCallback(
    async (id, param) => {
      if (!param) {
        return
      }
      await sendKinesisClickLogTimeoutAfter(() => {
        if (isMigratedToProtobuf(id)) {
          switch (id) {
            case 'review':
              CartOrderListPageLog.OrderProdInfoButtonWriteReviewBtn.click(param.kinesisLogMeta)
              break
            case 'self-checkin':
            case 'check-bus-tickets':
            case 'check-ticket':
            case 'voucher':
              CartOrderListPageLog.OrderProdInfoButtonQrBtn.click(param.kinesisLogMeta)
              break
            case 'nol-chat':
              CartOrderListPageLog.DmTalkButtonName.click(param.kinesisLogMeta)
              break
          }
          return
        }

        sendKinesisProductLinkClick({
          pageName: 'CartOrderList',
          id,
          hashOrderId: order?.hashOrderId || '',
          hashCategoryOrderId: param?.hashCategoryOrderId,
        })
      })

      /**
       * 주문 내역에서 주문 상세의 모달을 띄우는 경우 현재 주문 내역 정보 저장
       */
      storePreviewOrderListInfo()

      handleOrderItemButtonClick({
        id,
        param,
        orderId: order.id,
        parentOrderId: order.parentId,
        index,
      })
    },
    [order, index]
  )

  const onButtonImpressed = useCallback(
    (id, param) => {
      if (!param) {
        return
      }

      switch (id) {
        case 'review':
          CartOrderListPageLog.OrderProdInfoButtonWriteReviewBtn.impression(param.kinesisLogMeta)
          break
        case 'self-checkin':
        case 'check-bus-tickets':
        case 'check-ticket':
        case 'voucher':
          CartOrderListPageLog.OrderProdInfoButtonQrBtn.impression(param.kinesisLogMeta)
          break
        case 'nol-chat':
          CartOrderListPageLog.DmTalkButtonName.impression(param.kinesisLogMeta)
      }
    },
    [order]
  )

  const cardItems = useMemo(
    () =>
      order.categoryOrders
        .map(categoryOrderToCardOrder)
        .flat(1) // 레저 / 기차는 주문이 배열형태로 오기 때문에 nested 된 형태의 주문 정보를 전달받는다.
        .map((cardItem, index) => (
          <OrderListCardItem
            key={index}
            onClick={onProductClick}
            onImpressed={onProductImpr}
            className={'clickable'}
            onButtonClick={onButtonClick}
            onButtonImpressed={onButtonImpressed}
            showCancelPolicyRetry={true}
            onCancelRefreshButtonClick={() => onCancelRefreshButtonClick?.()}
            {...(cardItem as OrderCardItemProps)}
          />
        )),
    [order, currentAt, onButtonClick]
  )

  const cardItemsShown = useMemo(
    () => cardItems.slice(0, isShowMore ? undefined : DEFAULT_CARD_ITEM_COUNT),
    [cardItems, isShowMore]
  )

  const title = useIsoToFormat(order.createdAt, 'yyyy.MM.dd (ccc)')

  return (
    <NewCard>
      <CardTitle
        title={title}
        subTitle={''}
        fixedTopPosition={114}
        rightAppended={
          <OrderListTextButton
            title={'상세 보기'}
            type={'basic'}
            typography={'body1'}
            onClick={() => {
              /** 원주문이 존재하는 경우 원주문 상세보기로 이동 */
              const orderId = order.parentId ?? order.id
              onDetailsButtonClick?.(orderId)
            }}
            testId={`orderList-detail-button-${index}`}
            showArrow={true}
          />
        }
      />
      <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap' }}>{cardItemsShown}</div>

      {cardItems.length > DEFAULT_CARD_ITEM_COUNT && (
        <OrderListShowMore
          isShowMore={isShowMore}
          onClick={() => {
            if (!isShowMore) {
              CartOrderListPageLog.ButtonSeeAllBtn.click({
                hashOrderId: order.hashOrderId,
                addOrderPageYn: 'N',
              })
            }
            setIsShowMore(v => !v)
          }}
          onImpressed={() => {
            CartOrderListPageLog.ButtonSeeAllBtn.impression({
              hashOrderId: order.hashOrderId,
              addOrderPageYn: 'N',
            })
          }}
          remainingCount={cardItems.length - DEFAULT_CARD_ITEM_COUNT}
        />
      )}
    </NewCard>
  )
}

export default OrderListCard
